/**
 * 剧本监测 · 剧本绑定
 * 展示业务单元与飞书剧本表关联，支持绑定/解绑；不改动业务单元原页面。
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  App,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { LinkOutlined, PlusOutlined, ReloadOutlined } from "../components/icons";
import { trpc } from "../trpc";

const { Text, Title, Paragraph } = Typography;

type UnitRecord = Record<string, any>;

export function DispatchScriptBindPage() {
  const { message } = App.useApp();
  const [units, setUnits] = useState<UnitRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState<UnitRecord | null>(null);
  const [form] = Form.useForm();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const nextUnits = await trpc.fadan.businessUnits.list.query();
      setUnits(nextUnits);
    } catch (error: any) {
      message.error(error?.message || "加载业务单元失败");
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const unitOptions = useMemo(
    () => units.map((unit) => ({ value: unit.id as string, label: unit.name as string })),
    [units]
  );

  const openBind = (unit?: UnitRecord) => {
    setEditingUnit(unit ?? null);
    form.setFieldsValue({
      businessUnitId: unit?.id,
      url: unit?.scriptSheetUrl || "",
    });
    setModalOpen(true);
  };

  const handleSave = async (values: { businessUnitId: string; url: string }) => {
    setSaving(true);
    try {
      await trpc.fadan.businessUnits.bindScriptSheet.mutate({
        businessUnitId: values.businessUnitId,
        url: values.url.trim(),
      });
      message.success("剧本表格已绑定");
      setModalOpen(false);
      await loadData();
    } catch (error: any) {
      message.error(error?.message || "绑定失败");
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async (businessUnitId: string) => {
    setSaving(true);
    try {
      await trpc.fadan.businessUnits.clearScriptSheet.mutate({ businessUnitId });
      message.success("已解绑剧本表格");
      await loadData();
    } catch (error: any) {
      message.error(error?.message || "解绑失败");
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<UnitRecord> = [
    {
      title: "业务单元",
      dataIndex: "name",
      render: (name: string, record) => (
        <Space orientation="vertical" size={0}>
          <Text strong>{name}</Text>
          {record.description ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.description}
            </Text>
          ) : null}
        </Space>
      ),
    },
    {
      title: "状态",
      width: 100,
      render: (_, record) =>
        record.enabled ? <Tag color="success">启用</Tag> : <Tag>禁用</Tag>,
    },
    {
      title: "剧本表格",
      render: (_, record) =>
        record.scriptSheetUrl ? (
          <Text copyable={{ text: record.scriptSheetUrl }} style={{ wordBreak: "break-all" }}>
            {record.scriptSheetUrl}
          </Text>
        ) : (
          <Text type="secondary">未绑定</Text>
        ),
    },
    {
      title: "Token",
      width: 200,
      render: (_, record) =>
        record.scriptSpreadsheetToken ? (
          <Text type="secondary" copyable style={{ fontSize: 12 }}>
            {record.scriptSpreadsheetToken}
          </Text>
        ) : (
          "—"
        ),
    },
    {
      title: "绑定时间",
      width: 180,
      dataIndex: "scriptBoundAt",
      render: (value?: string) => value || "—",
    },
    {
      title: "操作",
      width: 180,
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" onClick={() => openBind(record)}>
            {record.scriptSpreadsheetToken ? "更换" : "绑定"}
          </Button>
          <Popconfirm
            title="解绑剧本表格？"
            okText="解绑"
            cancelText="取消"
            disabled={!record.scriptSpreadsheetToken}
            onConfirm={() => handleClear(record.id)}
          >
            <Button
              type="link"
              size="small"
              danger
              disabled={!record.scriptSpreadsheetToken}
              loading={saving}
            >
              解绑
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Space orientation="vertical" size={16} style={{ width: "100%" }}>
      <div className="page-title-row">
        <div>
          <Title level={3} style={{ marginBottom: 4 }}>
            <LinkOutlined style={{ marginRight: 8 }} />
            剧本绑定
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            每个业务单元绑定一张飞书剧本表；每天 5:00 或手动生成快照后按标题日期匹配当天剧本。
          </Paragraph>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>
            刷新
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openBind()}>
            新增关联
          </Button>
        </Space>
      </div>

      <Card variant="borderless" className="shadow-sm">
        <Table
          rowKey={(row) => row.id}
          loading={loading}
          columns={columns}
          dataSource={units}
          pagination={{ pageSize: 20, showSizeChanger: true }}
        />
      </Card>

      <Modal
        title={editingUnit ? `绑定剧本 · ${editingUnit.name}` : "新增剧本关联"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={saving}
        okText="绑定"
        destroyOnHidden
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item
            name="businessUnitId"
            label="业务单元"
            rules={[{ required: true, message: "请选择业务单元" }]}
          >
            <Select
              options={unitOptions}
              placeholder="选择业务单元"
              disabled={Boolean(editingUnit)}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item
            name="url"
            label="飞书剧本表格链接"
            rules={[{ required: true, message: "请输入飞书表格链接" }]}
          >
            <Input.TextArea
              placeholder="https://xxx.feishu.cn/sheets/xxxxxx"
              autoSize={{ minRows: 2, maxRows: 4 }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
